import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CanonicalSafetyScanReport } from '../components/safetyScanReport/CanonicalSafetyScanReport';
import {
  extractControlImplementation,
  presentScanControlRelation,
  type ScanControlRelationView,
} from '../domain/safetyCases/scanControlTemporalRelation';
import { resolveBuyerCustomerScanDownloadReportId } from '../domain/safetyCases/scanBuyerResultHierarchy';
import { presentCanonicalSafetyScanReport } from '../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import { presentScanVerificationWork } from '../domain/safetyScanReport/scanVerificationWork';
import { resolveCanonicalScanWindow, storedWindowFromPersistedReport } from '../domain/safetyScanReport/canonicalScanWindow';
import { resolveCanonicalLayerBMeasurementSource } from '../domain/safetyScanReport/canonicalStoredMeasurementBasis';
import { formatHumanScanWindow } from '../domain/acceptance/scanVerifyNavigation';
import {
  scanVerifyConfirmedCountLine,
  scanVerifyRateLine,
} from '../domain/acceptance/scanVerifyReconciliationCopy';
import { resolveBuyerPostFinalizeNavigation, resolvePostFinalizeRevision } from '../domain/acceptance/postFinalizeNavigation';
import { buildSafetyCaseControlsPath } from '../domain/safetyCases/investigateControlLink';
import { resolveCaseSignedRecordIdentity } from '../domain/safetyCases/workflowGlueNavigation';
import { buildExactSignedRecordPath } from '../domain/safetyCases/safetyCaseOverviewPresenter';
import { buildLiveSafetyCasePath } from '../utils/acceptanceHierarchyNavigation';
import {
  SIGN_THIS_SAFETY_SCAN,
  SIGN_THIS_SCAN_EXPLANATION,
  customerSafeFinalizeError,
  presentScanSigning,
} from '../domain/safetyCases/scanSigningPresenter';
import { useAuth } from '../contexts/AuthContext';
import { canSignSafetyScan } from '../auth/access';
import {
  honestClipMissingHelper,
  resolveHonestClipStatus,
} from '../domain/incidents/evidenceClipPresence';
import {
  resolvePreferredReviewMediaUri,
  selectPreferredReviewAttachment,
} from '../domain/incidents/preferredReviewMedia';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import { resolveConfiguredZoneLabel } from '../domain/customerLabels/customerFacingControlLabel';
import { api, ensureUiScope } from '../services/api';
import { ScanConditionLifecycleStory } from '../components/conditionLifecycle/ScanConditionLifecycleStory';
import { useScanConditionLifecycle } from '../components/conditionLifecycle/useScanConditionLifecycle';
import { incidentsApi, type Incident } from '../services/incidentsApi';
import {
  reportsApi,
  type ScanFormalDisposition,
  type ScanMeasurementReconciliation,
  type ScanVerificationMember,
  type ScanVerificationProgress,
  type ScanVerificationResponse,
} from '../services/reportsApi';

const CLEAR_REASONS = [
  { code: 'FALSE_POSITIVE', label: 'False positive' },
  { code: 'DUPLICATE', label: 'Duplicate' },
  { code: 'DEMO_OR_TEST', label: 'Demo or test' },
  { code: 'NOT_RELEVANT', label: 'Not relevant' },
  { code: 'OTHER', label: 'Other' },
] as const;

const UNRESOLVED_REASONS = [
  { code: 'INSUFFICIENT_EVIDENCE', label: 'Insufficient evidence' },
  { code: 'EVIDENCE_UNAVAILABLE', label: 'Evidence unavailable' },
  { code: 'CANNOT_DETERMINE', label: 'Cannot determine' },
  { code: 'OTHER', label: 'Other' },
] as const;

async function fetchMediaBlobUrl(uri: string): Promise<string> {
  const response = await api.get(uri, { responseType: 'blob' });
  return URL.createObjectURL(response.data as Blob);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

function humanizeToken(value?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return 'Unknown';
  return text.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function proposalDispositionLabel(disposition?: string | null): string {
  const value = String(disposition || '').toUpperCase();
  if (value === 'CONFIRM') return 'Confirm';
  if (value === 'CLEAR') return 'Clear';
  if (value === 'UNRESOLVED') return 'Unresolved';
  return humanizeToken(value);
}

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `verify-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function axiosErrorState(err: any): string {
  return String(err?.response?.data?.state || err?.response?.data?.error || '').trim();
}

function axiosErrorMessage(err: any, fallback: string): string {
  return String(
    err?.response?.data?.detail
    || err?.response?.data?.error
    || err?.message
    || fallback,
  );
}

function progressValue(progress?: ScanVerificationProgress | null): ScanVerificationProgress & {
  reviewed: number;
  toConfirm: number;
} {
  const work = presentScanVerificationWork(progress);
  return {
    total: work.total,
    unreviewed: work.unreviewed,
    confirmed: work.confirmed,
    cleared: work.cleared,
    unresolved: work.unresolved,
    complete: work.complete,
    reviewed: work.reviewed,
    toConfirm: work.toConfirm,
  };
}

export default function AcceptanceScanVerifyPage() {
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const reportId = String(reportIdParam || '').trim();
  const requestedMemberId = String(searchParams.get('incident_id') || searchParams.get('member_id') || '').trim();
  const siteTimeZone = useSiteTimeZone(String(searchParams.get('site_id') || '').trim() || null);
  const { effectiveUser } = useAuth();
  const canSign = canSignSafetyScan(effectiveUser);

  const [verification, setVerification] = useState<ScanVerificationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [clipUrl, setClipUrl] = useState('');
  const [stillUrl, setStillUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reasonPicker, setReasonPicker] = useState<'CLEAR' | 'UNRESOLVED' | null>(null);
  const [reconciliation, setReconciliation] = useState<ScanMeasurementReconciliation | null>(null);
  const [reconciliationError, setReconciliationError] = useState<string | null>(null);
  const [confirmSignOpen, setConfirmSignOpen] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [latestSignedId, setLatestSignedId] = useState<string | null>(null);
  const [controlRelation, setControlRelation] = useState<ScanControlRelationView | null>(null);
  const conditionLifecycle = useScanConditionLifecycle({
    reportId,
    siteId: String(searchParams.get('site_id') || '').trim(),
    requestedConditionId: String(searchParams.get('condition_id') || '').trim(),
    setSearchParams,
  });
  const [persistedReport, setPersistedReport] = useState<Record<string, unknown> | null>(null);
  const [caseScanHistory, setCaseScanHistory] = useState<Array<{
    report_id?: string | null;
    current_revision?: unknown;
    revisions?: Array<{ revision?: number | null }> | null;
    verification_state?: string | null;
    reviewed_observation_count?: number | null;
    scope_end?: string | null;
    window_end_utc?: string | null;
    signed_at?: string | null;
  }> | null>(null);
  const [latestScanCurrentRevision, setLatestScanCurrentRevision] = useState<unknown>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const applyVerification = useCallback((response: ScanVerificationResponse | null, preferMemberId?: string | null) => {
    setVerification(response);
    const members = Array.isArray(response?.members) ? response!.members! : [];
    const prefer = String(preferMemberId || '').trim();
    const nextUnreviewed = members.find((item) => String(item.disposition || 'UNREVIEWED') === 'UNREVIEWED');
    const nextId = (
      (prefer && members.some((item) => String(item.member_id || '') === prefer) ? prefer : '')
      || String(nextUnreviewed?.member_id || members[0]?.member_id || '')
    ).trim();
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (nextId) next.set('incident_id', nextId);
      else next.delete('incident_id');
      if (response?.safety_case_id && !next.get('safety_case_id')) {
        next.set('safety_case_id', String(response.safety_case_id));
      }
      const scope = ensureUiScope();
      if (scope.orgId && !next.get('org_id')) next.set('org_id', scope.orgId);
      if (scope.siteId && !next.get('site_id')) next.set('site_id', scope.siteId);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const loadVerification = useCallback(async (opts?: { preferMemberId?: string | null }) => {
    if (!reportId) return null;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await reportsApi.getScanVerification(reportId);
      applyVerification(response, opts?.preferMemberId);
      return response;
    } catch (err: any) {
      const state = axiosErrorState(err);
      const message = axiosErrorMessage(err, 'Failed to load Safety Scan verification');
      setLoadError(message);
      setVerification({
        ok: false,
        state: state === 'NO_SCAN' ? 'NO_SCAN' : 'LOAD_FAILED',
        error: message,
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [applyVerification, reportId]);

  useEffect(() => {
    const done = (
      Boolean(verification?.progress?.complete)
      && Boolean(verification?.verification_set_id)
      && String(verification?.state || '').toUpperCase() !== 'FINALIZED_IMMUTABLE'
      && String(verification?.report_status || '').toLowerCase() !== 'finalized'
    );
    if (!reportId || !done) {
      setReconciliation(null);
      setReconciliationError(null);
      return;
    }
    let cancelled = false;
    setReconciliation(null);
    setReconciliationError(null);
    void reportsApi.reconcileScanVerification(reportId)
      .then((result) => {
        if (!cancelled) setReconciliation(result);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setReconciliationError(axiosErrorMessage(err, 'Measurement reconciliation failed'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    reportId,
    verification?.verification_set_id,
    verification?.state,
    verification?.report_status,
    verification?.progress?.complete,
    verification?.progress?.confirmed,
    verification?.progress?.cleared,
    verification?.progress?.unresolved,
    verification?.progress?.unreviewed,
  ]);

  useEffect(() => {
    void loadVerification({ preferMemberId: requestedMemberId || null });
    // Initial load only; refresh is explicit after freeze/disposition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    if (!reportId) {
      setPersistedReport(null);
      return;
    }
    let cancelled = false;
    void Promise.all([
      reportsApi.openSafetyReport(reportId).catch(() => null),
      reportsApi.getSafetyReportArtifact(reportId).catch(() => null),
    ]).then(([opened, artifact]) => {
      if (cancelled) return;
      const fromOpen = opened && typeof opened === 'object'
        ? ((opened as { report?: Record<string, unknown> }).report || null)
        : null;
      const fromArtifact = (artifact?.persisted_report || null) as Record<string, unknown> | null;
      setPersistedReport((fromOpen as Record<string, unknown> | null) || fromArtifact);
    }).catch(() => {
      if (!cancelled) setPersistedReport(null);
    });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const members = useMemo(
    () => (Array.isArray(verification?.members) ? verification!.members! : []),
    [verification],
  );
  const currentItem: ScanVerificationMember | null = useMemo(() => {
    if (!members.length) return null;
    const match = members.find((item) => String(item.member_id || '') === requestedMemberId);
    return match || members[0] || null;
  }, [members, requestedMemberId]);
  const currentMemberId = String(currentItem?.member_id || '').trim();
  const currentDisposition = String(currentItem?.disposition || 'UNREVIEWED').toUpperCase() as ScanFormalDisposition;

  useEffect(() => {
    let cancelled = false;
    let clipObjectUrl = '';
    let stillObjectUrl = '';
    const run = async () => {
      setIncident(null);
      setClipUrl('');
      setStillUrl('');
      setActionError(null);
      setReasonPicker(null);
      if (!currentMemberId) return;
      setIncidentLoading(true);
      try {
        const row = await incidentsApi.getIncident(currentMemberId);
        if (cancelled) return;
        setIncident(row);
        const clipPick = selectPreferredReviewAttachment(row, 'video');
        const clipStatus = resolveHonestClipStatus(row, {
          hasClipArtifact: Boolean(clipPick?.filename || (row as any)?.clip_uri),
        });
        const clipUri = resolvePreferredReviewMediaUri(row, 'video');
        const stillUri = resolvePreferredReviewMediaUri(row, 'snapshot');
        if (clipStatus === 'AVAILABLE' && clipUri) {
          try {
            clipObjectUrl = clipUri.startsWith('blob:') || clipUri.startsWith('http')
              ? clipUri
              : await fetchMediaBlobUrl(clipUri);
            if (!cancelled) setClipUrl(clipObjectUrl);
          } catch {
            if (!cancelled) setClipUrl('');
          }
        }
        if (stillUri) {
          try {
            stillObjectUrl = stillUri.startsWith('blob:') || stillUri.startsWith('http')
              ? stillUri
              : await fetchMediaBlobUrl(stillUri);
            if (!cancelled) setStillUrl(stillObjectUrl);
          } catch {
            if (!cancelled) setStillUrl('');
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setActionError(axiosErrorMessage(err, 'Failed to load observation evidence'));
        }
      } finally {
        if (!cancelled) setIncidentLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
      if (String(clipObjectUrl || '').startsWith('blob:')) URL.revokeObjectURL(clipObjectUrl);
      if (String(stillObjectUrl || '').startsWith('blob:')) URL.revokeObjectURL(stillObjectUrl);
    };
  }, [currentMemberId]);

  const state = String(verification?.state || (loadError ? 'LOAD_FAILED' : '')).toUpperCase();
  const progress = progressValue(verification?.progress);
  const storedWindow = storedWindowFromPersistedReport(persistedReport as {
    window_start_utc?: string | null;
    window_end_utc?: string | null;
  });
  const canonicalWindow = resolveCanonicalScanWindow({
    storedWindowStart: storedWindow.start,
    storedWindowEnd: storedWindow.end,
    frozenWindowStart: verification?.window?.start,
    frozenWindowEnd: verification?.window?.end,
    caseTimeWindow: null,
    queueWindow: null,
    reportOpenWindow: null,
  });
  const windowLabel = formatHumanScanWindow(
    canonicalWindow.start,
    canonicalWindow.end,
    siteTimeZone,
  );
  const locationLabel = String(
    resolveConfiguredZoneLabel(
      verification?.location_label || verification?.scope?.zone_id || currentItem?.zone_id,
    )
    || verification?.location_label
    || verification?.scope?.zone_id
    || currentItem?.zone_id
    || 'Location not recorded',
  ).trim();
  const clipStatus = resolveHonestClipStatus(incident);
  const evidenceMissing = !clipUrl && !stillUrl && !incidentLoading && Boolean(currentMemberId);
  const safetyCaseId = String(
    searchParams.get('safety_case_id') || verification?.safety_case_id || '',
  ).trim();
  const frozen = state === 'FROZEN' || state === 'FROZEN_EMPTY' || state === 'FINALIZED_IMMUTABLE';
  const writable = Boolean(verification?.writable) && state === 'FROZEN';
  const pendingConfirmWork = writable && progress.toConfirm > 0;
  const previewCount = Number(verification?.preview?.eligible_count || 0);
  const signingView = presentScanSigning({
    reportId,
    reportStatus: verification?.report_status,
    verificationState: state,
    progressComplete: Boolean(progress.complete) && frozen,
    unreviewed: progress.unreviewed,
    unresolved: Number(reconciliation?.verification?.unresolved ?? progress.unresolved),
    unresolvedDisclosed: Boolean(reconciliation?.unresolved_disclosed) || Number(reconciliation?.verification?.unresolved || progress.unresolved) > 0,
    reconciliationReady: Boolean(reconciliation?.ok !== false && reconciliation),
    canSign,
    currentScanReportId: currentScanId,
    latestSignedReportId: latestSignedId,
  });

  const startVerification = useCallback(async () => {
    if (!reportId || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await reportsApi.startScanVerification(reportId);
      await loadVerification();
    } catch (err: any) {
      const apiState = axiosErrorState(err);
      setActionError(
        apiState === 'window_not_closed' || apiState === 'settlement_not_satisfied'
          ? 'Formal verification is not ready until the measurement window has closed and settlement is complete.'
          : axiosErrorMessage(err, 'Start verification failed'),
      );
    } finally {
      setBusy(false);
    }
  }, [busy, loadVerification, reportId]);

  const refreshCasePointers = useCallback(async () => {
    const caseId = String(searchParams.get('safety_case_id') || verification?.safety_case_id || '').trim();
    if (!caseId) return null;
    try {
      const opened = await reportsApi.openSafetyCase(caseId);
      const record = opened.safety_case || opened.finding;
      setCurrentScanId(String(record?.active_draft_report_id || '').trim() || null);
      setLatestSignedId(String(record?.latest_finalized_report_id || '').trim() || null);
      setCaseScanHistory((record as { scan_history?: typeof caseScanHistory } | null)?.scan_history || null);
      setLatestScanCurrentRevision(
        (record as { latest_scan_current_revision?: unknown } | null)?.latest_scan_current_revision ?? null,
      );
      const impl = extractControlImplementation(opened);
      setControlRelation(presentScanControlRelation({
        windowStart: canonicalWindow.start,
        windowEnd: canonicalWindow.end,
        controlRevisionId: impl.controlRevisionId,
        implementedAt: impl.implementedAt,
      }));
      return record;
    } catch {
      /* Case pointers are a display refresh only; signing already succeeded. */
      return null;
    }
  }, [searchParams, verification?.safety_case_id, canonicalWindow.start, canonicalWindow.end]);

  useEffect(() => {
    void refreshCasePointers();
  }, [refreshCasePointers]);

  const signThisScan = useCallback(async () => {
    if (!reportId || signBusy) return;
    setSignBusy(true);
    setSignError(null);
    try {
      const expectedVersion = Number(verification?.version || 1);
      const finalized = await reportsApi.finalizeSafetyReport(reportId, {
        expected_version: Number.isFinite(expectedVersion) ? expectedVersion : 1,
      });
      setConfirmSignOpen(false);
      const next = resolveBuyerPostFinalizeNavigation({
        reportId,
        revision: resolvePostFinalizeRevision(
          (finalized as { report?: Record<string, unknown> } | null)?.report || null,
        ),
        searchParams,
      });
      if (next?.path) {
        navigate(next.path);
        return;
      }
      await loadVerification();
      await refreshCasePointers();
    } catch (err: any) {
      const payload = err?.response?.data || {};
      const status = Number(err?.response?.status || 0);
      if (status === 403) {
        setSignError('You can inspect this Safety Scan, but you do not have permission to sign it.');
      } else if (String(payload.error || '') === 'report_not_draft') {
        setConfirmSignOpen(false);
        await loadVerification();
        await refreshCasePointers();
      } else {
        const shown = customerSafeFinalizeError(payload);
        setSignError(shown.message);
      }
    } finally {
      setSignBusy(false);
    }
  }, [loadVerification, navigate, refreshCasePointers, reportId, searchParams, signBusy, verification?.version]);

  const downloadCustomerSafetyScan = useCallback(async () => {
    if (!reportId || pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      let history = caseScanHistory;
      let signedId = latestSignedId;
      const openedComplete = Boolean(verification?.progress?.complete)
        && Number(verification?.progress?.unreviewed || 0) <= 0;
      if (!openedComplete) {
        const record = await refreshCasePointers();
        if (record) {
          history = (record as { scan_history?: typeof caseScanHistory }).scan_history || history;
          signedId = String(record.latest_finalized_report_id || signedId || '').trim() || signedId;
        }
      }
      const downloadReportId = resolveBuyerCustomerScanDownloadReportId({
        openedReportId: reportId,
        openedProgressComplete: Boolean(verification?.progress?.complete),
        openedUnreviewed: verification?.progress?.unreviewed,
        scanHistory: history,
        latestSignedReportId: signedId,
      }) || reportId;
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactPdf(downloadReportId, {
        face: 'customer_compact',
      });
      triggerBlobDownload(blob, filename);
    } catch {
      setPdfError('The Customer Safety Scan could not be downloaded.');
    } finally {
      setPdfBusy(false);
    }
  }, [
    caseScanHistory,
    latestSignedId,
    pdfBusy,
    refreshCasePointers,
    reportId,
    verification?.progress?.complete,
    verification?.progress?.unreviewed,
  ]);

  const submitDisposition = useCallback(async (
    disposition: 'CONFIRM' | 'CLEAR' | 'UNRESOLVED',
    reasonCode?: string,
  ) => {
    if (!reportId || !currentMemberId || busy || !writable) return;
    setBusy(true);
    setActionError(null);
    try {
      const expectedRevision = Number(currentItem?.revision || 0);
      const result = await reportsApi.recordScanVerificationDisposition(reportId, currentMemberId, {
        disposition,
        reason_code: reasonCode || undefined,
        expected_revision: Number.isFinite(expectedRevision) ? expectedRevision : 0,
        idempotency_key: newIdempotencyKey(),
      });
      const nextMembers = Array.isArray(result.members) && result.members.length
        ? result.members
        : members.map((item) => (
          String(item.member_id || '') === currentMemberId
            ? {
              ...item,
              disposition: result.current?.disposition || disposition,
              reason_code: result.current?.reason_code || reasonCode || null,
              revision: result.current?.revision,
              reviewer: result.current?.reviewer,
              decided_at: result.current?.decided_at,
            }
            : item
        ));
      const merged: ScanVerificationResponse = {
        ...verification,
        ...result,
        members: nextMembers,
        progress: result.progress || verification?.progress,
        state: result.state || verification?.state,
      };
      const nextUnreviewed = nextMembers.find((item) => (
        String(item.member_id || '') !== currentMemberId
        && String(item.disposition || 'UNREVIEWED') === 'UNREVIEWED'
      ));
      applyVerification(merged, String(nextUnreviewed?.member_id || currentMemberId));
      setReasonPicker(null);
    } catch (err: any) {
      const apiState = axiosErrorState(err);
      if (apiState === 'STALE_REVISION' || apiState === 'stale_revision') {
        setActionError('This item was updated in another session. Reloading the frozen verification set.');
        await loadVerification({ preferMemberId: currentMemberId });
      } else {
        setActionError(axiosErrorMessage(err, 'Disposition failed'));
      }
    } finally {
      setBusy(false);
    }
  }, [
    applyVerification,
    busy,
    currentItem?.revision,
    currentMemberId,
    loadVerification,
    members,
    reportId,
    verification,
    writable,
  ]);

  const remainingProposalCount = Number(verification?.proposals?.remaining || 0)
    || members.filter((item) => (
      String(item.disposition || 'UNREVIEWED') === 'UNREVIEWED' && Boolean(item.proposal)
    )).length;
  const currentProposal = currentDisposition === 'UNREVIEWED' ? currentItem?.proposal : null;

  const adoptProposal = useCallback(async () => {
    if (!reportId || !currentMemberId || busy || !writable || !currentProposal) return;
    setBusy(true);
    setActionError(null);
    try {
      const expectedRevision = Number(currentItem?.revision || 0);
      const result = await reportsApi.adoptScanVerificationProposal(reportId, currentMemberId, {
        expected_revision: Number.isFinite(expectedRevision) ? expectedRevision : 0,
        idempotency_key: newIdempotencyKey(),
      });
      const nextMembers = Array.isArray(result.members) && result.members.length
        ? result.members
        : members.map((item) => (
          String(item.member_id || '') === currentMemberId
            ? {
              ...item,
              disposition: result.current?.disposition || currentProposal.disposition,
              reason_code: result.current?.reason_code || currentProposal.reason_code || null,
              revision: result.current?.revision,
              reviewer: result.current?.reviewer,
              decided_at: result.current?.decided_at,
              proposal: null,
              adopted_from: result.current?.adopted_from || {
                source: 'INVESTIGATE',
                original_actor: currentProposal.original_actor,
                original_decided_at: currentProposal.original_decided_at,
                original_outcome: currentProposal.original_outcome,
              },
            }
            : item
        ));
      const merged: ScanVerificationResponse = {
        ...verification,
        ...result,
        members: nextMembers,
        progress: result.progress || verification?.progress,
        state: result.state || verification?.state,
        proposals: result.proposals || {
          remaining: Math.max(0, remainingProposalCount - 1),
          present: remainingProposalCount - 1 > 0,
        },
      };
      const nextUnreviewed = nextMembers.find((item) => (
        String(item.member_id || '') !== currentMemberId
        && String(item.disposition || 'UNREVIEWED') === 'UNREVIEWED'
      ));
      applyVerification(merged, String(nextUnreviewed?.member_id || currentMemberId));
      setReasonPicker(null);
    } catch (err: any) {
      const apiState = axiosErrorState(err);
      if (apiState === 'STALE_REVISION' || apiState === 'stale_revision') {
        setActionError('This item was updated in another session. Reloading the frozen verification set.');
        await loadVerification({ preferMemberId: currentMemberId });
      } else {
        setActionError(axiosErrorMessage(err, 'Adopt proposal failed'));
      }
    } finally {
      setBusy(false);
    }
  }, [
    applyVerification,
    busy,
    currentItem?.revision,
    currentMemberId,
    currentProposal,
    loadVerification,
    members,
    remainingProposalCount,
    reportId,
    verification,
    writable,
  ]);

  const adoptRemainingProposals = useCallback(async () => {
    if (!reportId || busy || !writable || remainingProposalCount < 1) return;
    setBusy(true);
    setActionError(null);
    try {
      const result = await reportsApi.adoptRemainingScanVerificationProposals(reportId, {
        expected_revision: 0,
        idempotency_key: newIdempotencyKey(),
      });
      if (Array.isArray(result.members) && result.members.length) {
        applyVerification({
          ...verification,
          ...result,
          members: result.members,
          progress: result.progress || verification?.progress,
          state: result.state || verification?.state,
        }, currentMemberId);
      } else {
        await loadVerification({ preferMemberId: currentMemberId });
      }
    } catch (err: any) {
      setActionError(axiosErrorMessage(err, 'Adopt remaining proposals failed'));
      await loadVerification({ preferMemberId: currentMemberId });
    } finally {
      setBusy(false);
    }
  }, [
    applyVerification,
    busy,
    currentMemberId,
    loadVerification,
    remainingProposalCount,
    reportId,
    verification,
    writable,
  ]);

  const backToCaseHref = safetyCaseId
    ? buildLiveSafetyCasePath({
      caseId: safetyCaseId,
      orgId: searchParams.get('org_id'),
      siteId: searchParams.get('site_id'),
    })
    : '/acceptance/cases';
  const controlsHref = safetyCaseId
    ? buildSafetyCaseControlsPath({
      safetyCaseId,
      orgId: searchParams.get('org_id'),
      siteId: searchParams.get('site_id'),
    })
    : '';
  const signedIdentity = resolveCaseSignedRecordIdentity({
    reportId,
    finalizedReportId: latestSignedId,
    latestScanCurrentRevision: reportId === String(latestSignedId || '').trim()
      ? latestScanCurrentRevision
      : undefined,
    scanHistory: caseScanHistory,
  });
  const signedRecordHref = (
    signingView.state === 'already_signed' || state === 'FINALIZED_IMMUTABLE'
  ) ? (buildExactSignedRecordPath({
      recordId: signedIdentity.recordId,
      revision: signedIdentity.revision ?? resolvePostFinalizeRevision(persistedReport),
      orgId: searchParams.get('org_id'),
      siteId: searchParams.get('site_id'),
    }) || '') : '';

  if (!reportId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Missing Safety Scan id.</Alert>
      </Box>
    );
  }

  const headerTitle = 'Verification workflow';
  const complete = frozen && progress.complete;
  const representativeEvidence = members
    .filter((item) => String(item.disposition || '').toUpperCase() === 'CONFIRM')
    .slice(0, 3)
    .map((item) => ({
      memberId: String(item.member_id || ''),
      disposition: 'CONFIRM',
      mediaAvailable: Boolean(stillUrl || clipUrl) && String(item.member_id || '') === currentMemberId,
      caption: `Confirmed observation ${String(item.member_id || '').slice(0, 8)}`,
    }));
  const reportView = presentCanonicalSafetyScanReport({
    displayedReportId: reportId,
    persistedReport,
    locationLabel,
    conditionRaw: verification?.scope?.risk_type || verification?.location_label,
    verification,
    reconciliation,
    controlRelation,
    signing: signingView,
    caseId: safetyCaseId,
    timeZone: siteTimeZone,
    evidence: representativeEvidence,
    workflowId: verification?.workflow_id,
    lifecycleStory: conditionLifecycle ? {
      alertsLinked: Boolean(
        conditionLifecycle.latestImmediateIncident || conditionLifecycle.latestTemporalPatternIncident,
      ),
      controlRecorded: Boolean(conditionLifecycle.controlRevisionId),
      sinceAvailable: Boolean(conditionLifecycle.sinceStartedAt),
      comparisonAvailable: Boolean(
        conditionLifecycle.latestScan?.beforeReportId && conditionLifecycle.latestScan?.afterReportId,
      ),
      resultDirection: conditionLifecycle.latestScan?.resultDirection || null,
    } : null,
  });
  const measurementSource = resolveCanonicalLayerBMeasurementSource({
    persisted: persistedReport,
    riskType: verification?.scope?.risk_type,
    workflowId: verification?.workflow_id,
    windowStart: canonicalWindow.start,
    windowEnd: canonicalWindow.end,
    siteFallback: searchParams.get('site_id'),
    deviceFallback: verification?.scope?.device_id,
    cameraFallback: verification?.scope?.camera_id,
    signed: state === 'FINALIZED_IMMUTABLE' || signingView.state === 'already_signed',
  });

  return (
    <Box sx={{ width: '100%', maxWidth: 980, mx: 'auto', p: { xs: 2, md: 3 } }} data-testid="scan-verify-page">
      <Stack spacing={2.5}>
        <CanonicalSafetyScanReport
          view={reportView}
          measurementSource={measurementSource}
        />

        {conditionLifecycle
          ? (
            <ScanConditionLifecycleStory
              lifecycle={conditionLifecycle}
              siteId={String(searchParams.get('site_id') || '').trim()}
              conditionId={String(searchParams.get('condition_id') || '').trim()}
              zoneId={verification?.scope?.zone_id}
              conditionFamily={verification?.scope?.risk_type}
              safetyCaseId={String(searchParams.get('safety_case_id') || verification?.safety_case_id || '').trim() || null}
              reportId={reportId}
              timeZone={siteTimeZone}
              onNavigate={(path) => navigate(path)}
            />
          )
          : null}

        <Box data-testid="scan-verification-workflow">
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.08 }}>
            {headerTitle}
          </Typography>
          <details>
            <summary>
              <Typography component="span" variant="caption" color="text.secondary">Technical identity</Typography>
            </summary>
            <Typography variant="body2" color="text.secondary" data-testid="scan-verify-report-id">
              Safety Scan {reportId}
            </Typography>
          </details>
          {signingView.isCurrentScan ? (
            <Chip size="small" color="primary" label="Current Scan" sx={{ mt: 0.75, mr: 0.75 }} data-testid="scan-verify-current-badge" />
          ) : (
            <Chip size="small" variant="outlined" label="Not Current Scan" sx={{ mt: 0.75, mr: 0.75 }} data-testid="scan-verify-not-current-badge" />
          )}
          {signingView.isLatestSigned ? (
            <Chip size="small" color="success" label="Latest Signed" sx={{ mt: 0.75 }} data-testid="scan-verify-latest-signed-badge" />
          ) : null}
          {frozen ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }} data-testid="scan-verify-progress">
              <Chip size="small" color="primary" label={`Total ${progress.total}`} data-testid="scan-verify-progress-total" />
              <Chip size="small" variant="outlined" label={`Reviewed ${progress.reviewed}`} data-testid="scan-verify-progress-reviewed" />
              <Chip size="small" variant="outlined" label={`To confirm ${progress.toConfirm}`} data-testid="scan-verify-progress-to-confirm" />
              <Chip size="small" variant="outlined" label={`Unreviewed ${progress.unreviewed}`} data-testid="scan-verify-progress-unreviewed" />
              <Chip size="small" variant="outlined" label={`Confirmed ${progress.confirmed}`} data-testid="scan-verify-progress-confirmed" />
              <Chip size="small" variant="outlined" label={`Cleared ${progress.cleared}`} data-testid="scan-verify-progress-cleared" />
              <Chip size="small" variant="outlined" label={`Unresolved ${progress.unresolved}`} data-testid="scan-verify-progress-unresolved" />
              <Chip size="small" variant="outlined" label={windowLabel} data-testid="scan-verify-window" />
              <Chip size="small" variant="outlined" label={humanizeToken(locationLabel)} />
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip size="small" variant="outlined" label={windowLabel} data-testid="scan-verify-window" />
              <Chip size="small" variant="outlined" label={humanizeToken(locationLabel)} />
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button component={RouterLink} to={backToCaseHref} variant="outlined">
            Back to Safety Case
          </Button>
          <Button
            variant="outlined"
            disabled={pdfBusy}
            onClick={() => void downloadCustomerSafetyScan()}
            data-testid="scan-verify-download-customer"
          >
            Download Customer Safety Scan
          </Button>
          {controlsHref ? (
            <Button component={RouterLink} to={controlsHref} variant="outlined" data-testid="scan-verify-open-controls">
              Open Controls
            </Button>
          ) : null}
          {signedRecordHref ? (
            <Button component={RouterLink} to={signedRecordHref} variant="outlined" data-testid="scan-verify-view-signed-record">
              View Signed Record
            </Button>
          ) : null}
          <Button variant="text" onClick={() => void loadVerification({ preferMemberId: requestedMemberId })}>
            Refresh
          </Button>
        </Stack>
        {pdfError ? <Alert severity="error" data-testid="scan-verify-pdf-error">{pdfError}</Alert> : null}

        {loading && !verification ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : null}

        {state === 'NO_SCAN' ? (
          <Alert severity="error" data-testid="scan-verify-no-scan">This Safety Scan was not found.</Alert>
        ) : null}
        {state === 'LOAD_FAILED' ? (
          <Alert severity="error" data-testid="scan-verify-load-failed">{loadError || 'Failed to load Safety Scan verification.'}</Alert>
        ) : null}
        {state === 'QUEUE_UNAVAILABLE' ? (
          <Alert severity="warning" data-testid="scan-verify-queue-unavailable">
            Formal verification is not available for this Safety Scan risk type.
          </Alert>
        ) : null}
        {state === 'FREEZE_NOT_READY' ? (
          <Alert severity="info" data-testid="scan-verify-freeze-not-ready">
            Formal verification is not ready until the measurement window has closed and settlement is complete.
            {previewCount > 0 ? ` ${previewCount} evidence items exist in this window, but they are not yet a frozen review population.` : ''}
          </Alert>
        ) : null}
        {state === 'NOT_STARTED' ? (
          <Alert severity="info" data-testid="scan-verify-not-started">
            Formal verification has not started. Evidence may already exist, but Confirm, Clear, and Unresolved
            are unavailable until the population is frozen.
            {previewCount > 0 ? ` Preview: ${previewCount} eligible evidence items.` : ''}
          </Alert>
        ) : null}
        {state === 'FROZEN_EMPTY' ? (
          <Alert severity="info" data-testid="scan-verify-frozen-empty">
            No evidence items were eligible for this Safety Scan.
          </Alert>
        ) : null}
        {complete && !reconciliation ? (
          <Alert severity="success" data-testid="scan-verify-complete">
            Verification complete. Measurement reconciliation pending.
          </Alert>
        ) : null}
        {complete && reconciliation ? (
          <Alert
            severity={reconciliation.measurement?.rate?.issuable ? 'success' : 'info'}
            data-testid="scan-verify-reconciled"
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Verification complete.</Typography>
            <Typography variant="body2" data-testid="scan-verify-reconciled-confirmed">
              {scanVerifyConfirmedCountLine(
                reconciliation.measurement,
                reconciliation.verification?.confirmed,
              )}
            </Typography>
            <Typography variant="body2" data-testid="scan-verify-reconciled-hours">
              Usable hours: {reconciliation.measurement?.denominator?.usable_hours == null
                ? 'not proven'
                : String(reconciliation.measurement.denominator.usable_hours)}
            </Typography>
            <Typography variant="body2" data-testid="scan-verify-reconciled-rate">
              {scanVerifyRateLine(reconciliation.measurement)}
            </Typography>
            {Number(reconciliation.verification?.unresolved || 0) > 0 ? (
              <Typography variant="body2" data-testid="scan-verify-reconciled-unresolved">
                Unresolved: {Number(reconciliation.verification?.unresolved)} (not included in the confirmed numerator)
              </Typography>
            ) : null}
            {Number(reconciliation.late_arrivals?.count || 0) > 0 ? (
              <Typography variant="body2" data-testid="scan-verify-reconciled-late">
                Late arrivals outside the frozen population: {Number(reconciliation.late_arrivals?.count)}.
                {reconciliation.late_arrivals?.claim_withheld_reason
                  ? ` Claim withheld (${reconciliation.late_arrivals.claim_withheld_reason}).`
                  : ''}
              </Typography>
            ) : null}
            <Typography variant="body2" data-testid="scan-verify-reconciled-gates">
              Coverage {reconciliation.measurement?.gates?.coverage_sufficient ? 'sufficient' : 'not sufficient'}
              {reconciliation.measurement?.gates?.rate_issuable ? '; rate issuable.' : '; rate not issuable.'}
            </Typography>
          </Alert>
        ) : null}
        {reconciliationError ? (
          <Alert severity="warning" data-testid="scan-verify-reconciliation-error">{reconciliationError}</Alert>
        ) : null}
        {state === 'FINALIZED_IMMUTABLE' || signingView.state === 'already_signed' ? (
          <Alert severity="warning" data-testid="scan-verify-finalized">
            This Safety Scan is signed. Formal dispositions can no longer be changed.
          </Alert>
        ) : null}
        {signingView.state === 'verification_required' && frozen && !progress.complete ? (
          <Alert severity="info" data-testid="scan-verify-sign-needs-verification">
            Complete formal verification for this Safety Scan before signing.
          </Alert>
        ) : null}
        {currentScanId || latestSignedId ? (
          <Stack spacing={0.25} data-testid="scan-verify-case-pointers">
            <Typography variant="body2" color="text.secondary" data-testid="scan-verify-current-scan">
              Current Scan: {currentScanId || 'None'}
            </Typography>
            <Typography variant="body2" color="text.secondary" data-testid="scan-verify-latest-signed">
              Latest Signed: {latestSignedId || 'None'}
            </Typography>
          </Stack>
        ) : null}
        {signingView.showSignCta ? (
          <Stack spacing={1} data-testid="scan-verify-sign-panel">
            <Typography variant="body2" color="text.secondary" data-testid="scan-verify-sign-copy">
              {SIGN_THIS_SCAN_EXPLANATION}
            </Typography>
            {signingView.unresolvedDisclosed ? (
              <Typography variant="body2" data-testid="scan-verify-sign-unresolved">
                Unresolved items remain disclosed and are not included in the confirmed numerator.
              </Typography>
            ) : null}
            {signError ? <Alert severity="error" data-testid="scan-verify-sign-error">{signError}</Alert> : null}
            <Button
              variant="contained"
              disabled={busy || signBusy}
              onClick={() => { setSignError(null); setConfirmSignOpen(true); }}
              data-testid="scan-verify-sign"
            >
              {SIGN_THIS_SAFETY_SCAN}
            </Button>
          </Stack>
        ) : null}

        {(state === 'NOT_STARTED' || state === 'FREEZE_NOT_READY') ? (
          <Button
            variant="contained"
            disabled={busy || state === 'FREEZE_NOT_READY'}
            onClick={() => void startVerification()}
            data-testid="scan-verify-start"
          >
            Start verification
          </Button>
        ) : null}

        {actionError ? <Alert severity="error" data-testid="scan-verify-action-error">{actionError}</Alert> : null}

        {writable && remainingProposalCount > 0 ? (
          <Alert severity="info" data-testid="scan-verify-proposals-banner">
            {remainingProposalCount} Investigate proposal{remainingProposalCount === 1 ? '' : 's'} can be adopted into this Safety Scan.
            They stay unreviewed until a named reviewer adopts them.
            <Box sx={{ mt: 1.25 }}>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => void adoptRemainingProposals()}
                data-testid="scan-verify-adopt-remaining"
              >
                Adopt remaining proposals
              </Button>
            </Box>
          </Alert>
        ) : null}

        {Number(verification?.late_arrivals?.count || 0) > 0 ? (
          <Alert severity="warning" data-testid="scan-verify-late-arrivals">
            {Number(verification?.late_arrivals?.count)} late arrival
            {Number(verification?.late_arrivals?.count) === 1 ? '' : 's'} noticed after freeze.
            They are outside this frozen population and are not review tasks.
          </Alert>
        ) : null}

        {frozen && members.length ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="scan-verify-member-list">
            {members.map((item) => {
              const memberId = String(item.member_id || '');
              const disposition = String(item.disposition || 'UNREVIEWED').toUpperCase();
              const selected = memberId === currentMemberId;
              return (
                <Chip
                  key={memberId}
                  size="small"
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                  label={`${item.ordinal || '?'} ${disposition}${item.proposal && disposition === 'UNREVIEWED' ? ' · proposed' : ''}`}
                  onClick={() => applyVerification(verification, memberId)}
                  data-testid={`scan-verify-member-${memberId}`}
                />
              );
            })}
          </Stack>
        ) : null}

        {frozen && currentMemberId ? (
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }} data-testid="scan-verify-item">
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {humanizeToken(currentItem?.incident_type || (incident as any)?.incident_type)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatHumanScanWindow(currentItem?.event_ts || null, null, siteTimeZone)}
                  {locationLabel ? ` · ${humanizeToken(locationLabel)}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75 }} data-testid="scan-verify-current-disposition">
                  Disposition: {currentDisposition}
                  {currentItem?.reason_code ? ` · ${humanizeToken(currentItem.reason_code)}` : ''}
                  {currentItem?.reviewer?.display ? ` · ${currentItem.reviewer.display}` : ''}
                  {currentItem?.decided_at ? ` · ${formatHumanScanWindow(currentItem.decided_at, null, siteTimeZone)}` : ''}
                </Typography>
                {currentProposal ? (
                  <Alert severity="info" sx={{ mt: 1.25 }} data-testid="scan-verify-proposal">
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Proposed from Investigate
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {proposalDispositionLabel(currentProposal.disposition)}
                      {currentProposal.reason ? ` · ${humanizeToken(currentProposal.reason)}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reviewed previously by {currentProposal.original_actor || 'an Investigate reviewer'}
                      {currentProposal.original_decided_at
                        ? ` · ${formatHumanScanWindow(currentProposal.original_decided_at, null, siteTimeZone)}`
                        : ''}
                    </Typography>
                    {writable ? (
                      <Button
                        sx={{ mt: 1.25, fontWeight: 800 }}
                        variant="contained"
                        disabled={busy}
                        onClick={() => void adoptProposal()}
                        data-testid="scan-verify-adopt"
                      >
                        Adopt
                      </Button>
                    ) : null}
                  </Alert>
                ) : null}
              </Box>

              {incidentLoading ? (
                <Stack alignItems="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  {clipUrl ? (
                    <Box
                      component="video"
                      src={clipUrl}
                      controls
                      sx={{ width: '100%', maxHeight: 420, bgcolor: '#0b1220', borderRadius: 2 }}
                      data-testid="scan-verify-clip"
                    />
                  ) : null}
                  {!clipUrl && stillUrl ? (
                    <Box
                      component="img"
                      src={stillUrl}
                      alt="Observation snapshot"
                      sx={{ width: '100%', maxHeight: 420, objectFit: 'contain', borderRadius: 2, bgcolor: '#0b1220' }}
                      data-testid="scan-verify-still"
                    />
                  ) : null}
                  {incident && (evidenceMissing || (!clipUrl && clipStatus !== 'AVAILABLE' && !stillUrl)) ? (
                    <Alert severity="warning" data-testid="scan-verify-evidence-unavailable">
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Evidence unavailable</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {honestClipMissingHelper()}
                      </Typography>
                      {writable ? (
                        <Button
                          sx={{ mt: 1.25, fontWeight: 800 }}
                          variant="contained"
                          color="warning"
                          disabled={busy}
                          onClick={() => void submitDisposition('UNRESOLVED', 'EVIDENCE_UNAVAILABLE')}
                          data-testid="scan-verify-unresolved-evidence-unavailable"
                        >
                          Unresolved — evidence unavailable
                        </Button>
                      ) : null}
                    </Alert>
                  ) : null}
                  {clipUrl && stillUrl ? (
                    <Box
                      component="img"
                      src={stillUrl}
                      alt="Snapshot"
                      sx={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 2, bgcolor: '#111827' }}
                    />
                  ) : null}
                </Stack>
              )}

              {pendingConfirmWork ? (
                <Stack spacing={1} data-testid="scan-verify-actions">
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Review</Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    <Button
                      variant="contained"
                      disabled={busy || evidenceMissing}
                      onClick={() => void submitDisposition('CONFIRM')}
                      data-testid="scan-verify-confirm"
                    >
                      Confirm
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      disabled={busy || evidenceMissing}
                      onClick={() => setReasonPicker('CLEAR')}
                      data-testid="scan-verify-clear"
                    >
                      Clear
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={busy}
                      onClick={() => setReasonPicker('UNRESOLVED')}
                      data-testid="scan-verify-unresolved"
                    >
                      Unresolved
                    </Button>
                  </Stack>
                  {reasonPicker === 'CLEAR' ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="scan-verify-clear-reasons">
                      {CLEAR_REASONS.map((reason) => (
                        <Button
                          key={reason.code}
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => void submitDisposition('CLEAR', reason.code)}
                          data-testid={`scan-verify-reason-${reason.code}`}
                        >
                          {reason.label}
                        </Button>
                      ))}
                    </Stack>
                  ) : null}
                  {reasonPicker === 'UNRESOLVED' ? (
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="scan-verify-unresolved-reasons">
                      {UNRESOLVED_REASONS.map((reason) => (
                        <Button
                          key={reason.code}
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          onClick={() => void submitDisposition('UNRESOLVED', reason.code)}
                          data-testid={`scan-verify-reason-${reason.code}`}
                        >
                          {reason.label}
                        </Button>
                      ))}
                    </Stack>
                  ) : null}
                  <Typography variant="caption" color="text.secondary">
                    Confirm stays blocked when evidence is missing. Unresolved is reviewed and is never counted as confirmed.
                    Previously reviewed items remain revisable until signing. Investigate proposals are informational until adopted.
                  </Typography>
                </Stack>
              ) : null}

              <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>More / Advanced</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Report id: {reportId}</Typography>
                    <Typography variant="caption" color="text.secondary">Observation id: {currentMemberId}</Typography>
                    <Typography variant="caption" color="text.secondary">Workflow id: {String(verification?.workflow_id || '—')}</Typography>
                    <Typography variant="caption" color="text.secondary">Clip status: {clipStatus}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Population digest: {String(verification?.freeze?.population_digest || '—')}
                    </Typography>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Paper>
        ) : null}

        <Dialog
          open={confirmSignOpen}
          onClose={() => { if (!signBusy) setConfirmSignOpen(false); }}
          data-testid="scan-verify-sign-dialog"
        >
          <DialogTitle>{SIGN_THIS_SAFETY_SCAN}</DialogTitle>
          <DialogContent>
            <Stack spacing={1} sx={{ pt: 0.5 }}>
              <Typography variant="body2" data-testid="scan-verify-sign-dialog-window">
                Scan window: {windowLabel}
              </Typography>
              <Typography variant="body2" data-testid="scan-verify-sign-dialog-report">
                Safety Scan: {reportId}
              </Typography>
              <Typography variant="body2" data-testid="scan-verify-sign-dialog-copy">
                {SIGN_THIS_SCAN_EXPLANATION}
              </Typography>
              {signingView.unresolvedDisclosed ? (
                <Typography variant="body2" data-testid="scan-verify-sign-dialog-unresolved">
                  Unresolved items will be disclosed on the Signed Record and will not enter the confirmed numerator.
                </Typography>
              ) : null}
              {signError ? <Alert severity="error">{signError}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmSignOpen(false)} disabled={signBusy}>Cancel</Button>
            <Button
              variant="contained"
              disabled={signBusy}
              onClick={() => void signThisScan()}
              data-testid="scan-verify-sign-confirm"
            >
              {signBusy ? 'Signing…' : SIGN_THIS_SAFETY_SCAN}
            </Button>
          </DialogActions>
        </Dialog>
        </Box>
      </Stack>
    </Box>
  );
}
